import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/services/api";
import { useLang } from "@/context/LanguageContext";
import { fmtMoney, fmtDate } from "@/utils/format";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";

export default function WithdrawalsPage() {
  const { t } = useLang();
  const qc = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["withdrawals"],
    queryFn: async () => (await api.get("/withdrawals")).data,
  });

  const invalidateFinance = () => {
    qc.invalidateQueries({ queryKey: ["withdrawals"] });
    qc.invalidateQueries({ queryKey: ["control-summary"] });
    qc.invalidateQueries({ queryKey: ["control-week"] });
    qc.invalidateQueries({ queryKey: ["agents"] });
  };

  const actionMut = useSingleFlightMutation({
    mutationFn: async ({ id, action }) => (await api.put(`/withdrawals/${id}`, { action })).data,
    onSuccess: (_data, vars) => {
      toast.success(
        vars?.action === "paid"
          ? "Retrè peye — soti nan Caisse Admin"
          : "Retrè mete ajou",
      );
      invalidateFinance();
    },
  });

  const cleanupMut = useMutation({
    mutationFn: async () => (await api.post("/withdrawals/cleanup-test-negatives")).data,
    onSuccess: (data) => {
      const n = data?.removed_count ?? 0;
      const restored = data?.caisse_restored ?? 0;
      toast.success(
        n
          ? `Retire ${n} retrè tès · Caisse +${fmtMoney(restored)}`
          : "Pa gen retrè tès pou retire",
      );
      invalidateFinance();
    },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/withdrawals/${id}`)).data,
    onSuccess: (data) => {
      toast.success(
        data?.caisse_restored
          ? "Retrè efase — kob retounen nan Caisse"
          : "Retrè efase",
      );
      invalidateFinance();
    },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t("withdrawals")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Apwouve oswa peye demann ajan yo</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-2 border-rose-500/30 text-rose-600 hover:bg-rose-500/10"
            disabled={cleanupMut.isPending}
            data-testid="cleanup-test-withdrawals-btn"
            onClick={() => {
              if (!window.confirm("Retire retrè tès ki te koz commission negatif (ak ajan ki pa egziste)?")) return;
              cleanupMut.mutate();
            }}
          >
            <Trash2 className="h-4 w-4" /> Netwaye tès negatif
          </Button>
          <Button
            variant="outline"
            data-testid="export-withdrawals-btn"
            className="gap-2"
            onClick={async () => {
              const token = localStorage.getItem("tgs-token");
              const res = await fetch(`${API}/export/withdrawals.csv`, { headers: { Authorization: `Bearer ${token}` } });
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "tgs_withdrawals.csv"; a.click(); URL.revokeObjectURL(url);
              toast.success("CSV telechaje");
            }}
          >
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ajan</TableHead>
              <TableHead>{t("phone")}</TableHead>
              <TableHead>Metòd</TableHead>
              <TableHead>{t("amount")}</TableHead>
              <TableHead>Dat</TableHead>
              <TableHead>Estati</TableHead>
              <TableHead className="text-right">Aksyon</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && [...Array(3)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>
            ))}
            {!isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Pa gen retrè</TableCell></TableRow>
            )}
            {rows.map((w) => (
              <TableRow key={w.id} data-testid={`withdraw-row-${w.id}`}>
                <TableCell className="font-medium">{w.agent_name || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{w.agent_phone || "—"}</TableCell>
                <TableCell>
                  <div className="font-medium">{w.method}</div>
                  {w.natcash_number && <div className="text-xs text-muted-foreground">{w.natcash_number}</div>}
                  {w.natcash_name && <div className="text-xs text-muted-foreground">{w.natcash_name}</div>}
                </TableCell>
                <TableCell className="font-semibold">{fmtMoney(w.amount)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(w.created_at)}</TableCell>
                <TableCell><StatusBadge status={w.status} /></TableCell>
                <TableCell className="text-right space-x-1">
                  {w.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => actionMut.mutate({ id: w.id, action: "approve" })} data-testid={`approve-${w.id}`}>
                        {t("approve")}
                      </Button>
                      <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-500 hover:bg-rose-500/10" onClick={() => actionMut.mutate({ id: w.id, action: "reject" })} data-testid={`reject-${w.id}`}>
                        {t("reject")}
                      </Button>
                    </>
                  )}
                  {(w.status === "approved" || w.status === "pending") && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => actionMut.mutate({ id: w.id, action: "paid" })} data-testid={`pay-${w.id}`}>
                      {t("mark_paid")}
                    </Button>
                  )}
                  {(w.status === "paid" || w.status === "rejected") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-wd-${w.id}`}
                      onClick={() => {
                        if (!window.confirm("Efase retrè sa a? Si li te peye, kob a retounen nan Caisse.")) return;
                        deleteMut.mutate(w.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
